import axios, { AxiosError, type AxiosRequestConfig, type InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '@/lib/auth-store';
import { OFFLINE_MUTATION_CODE } from '@/lib/api-error-code';

export const api = axios.create({ baseURL: '', timeout: 15_000, withCredentials: true });
export { OFFLINE_MUTATION_CODE } from '@/lib/api-error-code';

export const AI_DATA_CONSENT_REQUIRED_DETAIL = 'AI data processing consent required';
export const AI_DATA_CONSENT_REQUIRED_EVENT = 'aniphys:ai-consent-required';

export function isAiDataConsentRequiredError(error: unknown): boolean {
  if (axios.isAxiosError(error)) {
    const detail = (error.response?.data as { detail?: unknown } | undefined)?.detail;
    return error.response?.status === 403
      && typeof detail === 'string'
      && detail.trim() === AI_DATA_CONSENT_REQUIRED_DETAIL;
  }

  const candidate = error as { status?: unknown; detail?: unknown } | null;
  return candidate?.status === 403
    && typeof candidate.detail === 'string'
    && candidate.detail.trim() === AI_DATA_CONSENT_REQUIRED_DETAIL;
}

export function createRequestId(): string {
  return globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

const MUTATION_METHODS = new Set(['post', 'put', 'patch', 'delete']);

export function guardOfflineMutation(config: InternalAxiosRequestConfig): InternalAxiosRequestConfig {
  const method = config.method?.toLowerCase() ?? 'get';
  if (
    MUTATION_METHODS.has(method)
    && typeof navigator !== 'undefined'
    && navigator.onLine === false
  ) {
    throw new AxiosError(
      'Mutation blocked while offline',
      OFFLINE_MUTATION_CODE,
      config,
    );
  }
  return config;
}

api.interceptors.request.use((config) => {
  guardOfflineMutation(config);
  const token = useAuthStore.getState().accessToken;
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

let refreshInFlight: Promise<string | null> | null = null;

async function performRefresh(): Promise<string | null> {
  if (!useAuthStore.getState().hasSession) return null;
  try {
    const { data } = await axios.post(
      '/api/v1/auth/refresh',
      undefined,
      { withCredentials: true },
    );
    const next = data?.access_token as string | undefined;
    if (!next || !useAuthStore.getState().hasSession) {
      useAuthStore.getState().clear();
      return null;
    }
    useAuthStore.getState().setAccessToken(next);
    return next;
  } catch {
    useAuthStore.getState().clear();
    return null;
  }
}

export function refreshSession(): Promise<string | null> {
  refreshInFlight ??= performRefresh().finally(() => { refreshInFlight = null; });
  return refreshInFlight;
}

api.interceptors.response.use(
  (r) => r,
  async (err: AxiosError) => {
    const cfg = err.config as (AxiosRequestConfig & { _retried?: boolean }) | undefined;
    const url = cfg?.url?.split('?', 1)[0] ?? '';
    if (
      /^\/api\/v1\/ai(?:\/|$)/.test(url)
      && isAiDataConsentRequiredError(err)
      && typeof window !== 'undefined'
    ) {
      window.dispatchEvent(new window.Event(AI_DATA_CONSENT_REQUIRED_EVENT));
    }
    if (err.response?.status === 401 && cfg && !cfg._retried && !cfg.url?.includes('/auth/refresh')) {
      cfg._retried = true;
      const token = await refreshSession();
      if (token) {
        cfg.headers = { ...(cfg.headers ?? {}), Authorization: `Bearer ${token}` };
        return api.request(cfg);
      }
    }
    throw err;
  },
);
